/**
 * Autopilot list (spec §7, /autopilot): the adjudicator's sampling surface.
 * Runs with the human outcome joined, conflicts first, filters as the API,
 * an "unadjudicated sample" button for a fair draw from the agreements, and
 * the per-class benchmark table. L2/admin only: the API returns 403 for
 * everyone else and the page says so.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, RefreshCw, Shuffle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/ui/Pagination';
import { SeverityBadge } from '../components/ui/SeverityBadge';
import { formatDateTime } from '../lib/utils';
import { getTenants } from '../services';
import { getAutopilotRuns, getAutopilotSample, getAutopilotSummary, DISPOSITION_LABEL, ADJUDICATION_LABEL, AGREEMENT_STYLE, AGREEMENT_LABEL, type AutopilotListRow, type AutopilotClassSummary , EXECUTION_LABEL, EXECUTION_STYLE } from '../services/autopilot';
import { Chip, DispositionChip } from '../components/autopilot/AutopilotBanner';
import type { Tenant, Severity } from '../data/types';

const PAGE_SIZE = 50;

export function Autopilot() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState('');
  const [rule, setRule] = useState('');
  const [disposition, setDisposition] = useState('');
  const [agreement, setAgreement] = useState('');
  const [adjudicated, setAdjudicated] = useState<'' | 'true' | 'false'>('');
  const [execution, setExecution] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<AutopilotListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<AutopilotClassSummary[]>([]);
  const [totals, setTotals] = useState<{ runs: number; adjudicated: number; shouldHaveEscalated: number; agreement: Record<string, number> } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ code?: string; message: string } | null>(null);
  const [sampling, setSampling] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => { getTenants().then((r) => { if (r.success) setTenants(r.data); }); }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [list, sum] = await Promise.all([
      getAutopilotRuns({ tenantId: tenantId || undefined, rule: rule || undefined, disposition: disposition || undefined, agreement: agreement || undefined, adjudicated: adjudicated || undefined, execution: execution || undefined, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
      getAutopilotSummary({ tenantId: tenantId || undefined }),
    ]);
    setLoading(false);
    if (!list.success) { setError({ code: (list.errorBody as { code?: string } | undefined)?.code, message: list.error ?? 'Could not load Autopilot runs' }); return; }
    setRows(list.data.runs); setTotal(list.data.pagination.total);
    if (sum.success) { setSummary(sum.data.summary); setTotals(sum.data.totals); }
  }, [tenantId, rule, disposition, agreement, adjudicated, execution, page]);

  useEffect(() => { void load(); }, [load]);

  const sample = async () => {
    setSampling(true);
    const r = await getAutopilotSample();
    setSampling(false);
    if (r.success && r.data.runs[0]) {
      const run = r.data.runs[0];
      window.location.assign(`/incidents/${run.tenantId}/${run.incidentId}?section=autopilot`);
    } else {
      setError({ message: 'No unadjudicated agreements left in the top classes' });
    }
  };

  if (error?.code === 'AUTOPILOT_L2_ONLY') {
    return <div className="rounded-xl border border-border-default bg-bg-secondary p-6 text-sm text-text-secondary">Autopilot records are visible to L2 and admin only.</div>;
  }
  if (error?.code === 'AUTOPILOT_NOT_PROVISIONED') {
    return <div className="rounded-xl border border-border-default bg-bg-secondary p-6 text-sm text-text-secondary">Autopilot storage is not provisioned in this environment yet.</div>;
  }

  const select = 'rounded-lg border border-border-default bg-bg-primary px-2 py-1.5 text-sm text-text-primary';

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2"><Bot className="w-6 h-6" /> Autopilot</h1>
          <p className="text-sm text-text-secondary">What the platform would have done, next to what people did. Benchmark only — nothing here acts.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => void load()} disabled={loading}><RefreshCw className="w-4 h-4" /> Refresh</Button>
          <Button variant="primary" size="sm" onClick={sample} disabled={sampling}><Shuffle className="w-4 h-4" /> Unadjudicated sample</Button>
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ['Runs', totals.runs], ['Agree', totals.agreement.agree ?? 0], ['Conflict', totals.agreement.conflict ?? 0], ['Adjudicated', totals.adjudicated], ['Should have escalated', totals.shouldHaveEscalated],
          ].map(([label, value]) => (
            <div key={String(label)} className="rounded-xl border border-border-default bg-bg-secondary p-3"><div className="text-xs text-text-secondary">{label}</div><div className="text-xl font-semibold text-text-primary">{value}</div></div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <select className={select} value={tenantId} onChange={(e) => { setTenantId(e.target.value); setPage(1); }}>
          <option value="">All customers</option>
          {tenants.map((t) => <option key={t.tenantId} value={t.tenantId}>{t.displayName}</option>)}
        </select>
        <input className={select} placeholder="Rule (title starts with)" value={rule} onChange={(e) => { setRule(e.target.value); setPage(1); }} />
        <select className={select} value={disposition} onChange={(e) => { setDisposition(e.target.value); setPage(1); }}>
          <option value="">Any disposition</option>
          {Object.entries(DISPOSITION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className={select} value={agreement} onChange={(e) => { setAgreement(e.target.value); setPage(1); }}>
          <option value="">Any agreement</option><option value="conflict">Conflict</option><option value="agree">Agree</option><option value="open">Open</option>
        </select>
        <select className={select} value={adjudicated} onChange={(e) => { setAdjudicated(e.target.value as '' | 'true' | 'false'); setPage(1); }}>
          <option value="">Adjudicated or not</option><option value="false">Not adjudicated</option><option value="true">Adjudicated</option>
        </select>
        <select className={select} value={execution} onChange={(e) => { setExecution(e.target.value); setPage(1); }} title="What the executor did with the verdict (auto-close policy)">
          <option value="">Any execution</option><option value="executed">Closed by Autopilot</option><option value="would-execute">Would execute</option><option value="not-eligible">Not eligible</option><option value="paused">Paused</option><option value="disabled">Policy disabled</option><option value="pending">Not evaluated</option>
        </select>
        <button className="text-sm text-accent-blue hover:underline ml-auto" onClick={() => setShowSummary(!showSummary)}>{showSummary ? 'Hide' : 'Show'} benchmark by class</button>
      </div>

      {showSummary && (
        <div className="rounded-xl border border-border-default bg-bg-secondary overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-text-secondary bg-bg-tertiary"><tr>
              <th className="px-3 py-2 text-left">Customer</th><th className="px-3 py-2 text-left">Rule</th><th className="px-3 py-2 text-left">Severity</th><th className="px-3 py-2 text-right">Runs</th>
              <th className="px-3 py-2 text-right">Close / Hold / Page</th><th className="px-3 py-2 text-right">Agreement</th><th className="px-3 py-2 text-right">Adjudicated</th><th className="px-3 py-2 text-right">Should have escalated</th>
            </tr></thead>
            <tbody>
              {summary.map((c) => (
                <tr key={`${c.tenantId}|${c.rule}|${c.severity}`} className="border-t border-border-default">
                  <td className="px-3 py-2">{c.tenantDisplayName}</td>
                  <td className="px-3 py-2"><button className="text-accent-blue hover:underline text-left" onClick={() => { setRule(c.rule); setTenantId(c.tenantId); setPage(1); }}>{c.rule}</button></td>
                  <td className="px-3 py-2 capitalize">{c.severity}</td>
                  <td className="px-3 py-2 text-right">{c.runs}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{c.dispositions['would-close'] ?? 0} / {c.dispositions['would-hold-for-l2'] ?? 0} / {c.dispositions['would-page'] ?? 0}</td>
                  <td className="px-3 py-2 text-right">{c.agreementRate === null ? '—' : `${c.agreementRate}%`}<span className="text-xs text-text-secondary"> ({c.agreement.agree ?? 0}/{(c.agreement.agree ?? 0) + (c.agreement.conflict ?? 0)})</span></td>
                  <td className="px-3 py-2 text-right">{c.adjudicated}</td>
                  <td className={c.shouldHaveEscalated > 0 ? 'px-3 py-2 text-right text-accent-red font-medium' : 'px-3 py-2 text-right'}>{c.shouldHaveEscalated}</td>
                </tr>
              ))}
              {summary.length === 0 && <tr><td colSpan={8} className="px-3 py-4 text-center text-text-secondary">No runs yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {error && <div className="text-sm text-accent-red">{error.message}</div>}

      <div className="rounded-xl border border-border-default bg-bg-secondary overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-text-secondary bg-bg-tertiary"><tr>
            <th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Customer</th><th className="px-3 py-2 text-left">Incident</th><th className="px-3 py-2 text-left">Severity</th>
            <th className="px-3 py-2 text-left">Autopilot</th><th className="px-3 py-2 text-left">Human</th><th className="px-3 py-2 text-left">Agreement</th><th className="px-3 py-2 text-left">Adjudication</th>
          </tr></thead>
          <tbody>
            {loading && rows.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-text-secondary">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-text-secondary">No runs match</td></tr>}
            {rows.map((r) => (
              <tr key={r.runId} className="border-t border-border-default hover:bg-bg-tertiary/50">
                <td className="px-3 py-2 text-xs text-text-secondary whitespace-nowrap">{formatDateTime(r.createdAt)}</td>
                <td className="px-3 py-2">{r.tenantDisplayName}</td>
                <td className="px-3 py-2"><Link className="text-accent-blue hover:underline" to={`/incidents/${r.tenantId}/${r.incidentId}?section=autopilot`}>#{r.incidentId}</Link><div className="text-xs text-text-secondary max-w-xs truncate" title={r.title ?? ''}>{r.rule}</div></td>
                <td className="px-3 py-2">{r.severity && <SeverityBadge severity={r.severity.toLowerCase() as Severity} showPulse={false} />}</td>
                <td className="px-3 py-2"><DispositionChip disposition={r.disposition} /><div className="text-xs text-text-secondary mt-1">r{r.riskScore ?? '—'} · {r.confidence} · {r.rounds} round(s), {r.queriesRun} queries{r.modelDisposition !== r.disposition ? ` · model: ${DISPOSITION_LABEL[r.modelDisposition]}` : ''}</div>{r.execution && <div className="mt-1"><Chip className={EXECUTION_STYLE[r.execution.decision]} title={r.execution.reason ?? undefined}>{EXECUTION_LABEL[r.execution.decision]}</Chip></div>}</td>
                <td className="px-3 py-2 text-xs">{r.human.platformStatus ?? '—'}{r.human.classification ? <div className="text-text-secondary">{r.human.classification} / {r.human.determination ?? '—'}</div> : null}{r.human.assignedToName && <div className="text-text-secondary">{r.human.assignedToName}</div>}</td>
                <td className="px-3 py-2"><Chip className={AGREEMENT_STYLE[r.agreement]}>{AGREEMENT_LABEL[r.agreement]}</Chip></td>
                <td className="px-3 py-2 text-xs">{r.adjudication ? <span>{ADJUDICATION_LABEL[r.adjudication.verdict]}<div className="text-text-secondary">{r.adjudication.by}</div></span> : <span className="text-text-secondary">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} showPageNumbers />
    </div>
  );
}
